import React, {
  useState,
  useLayoutEffect,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { usePopper } from 'react-popper';

import RelatedRecords from './related_records';
import styles from './related_records_opener.module.css';
import classNames from 'classnames';
import { connect } from 'react-redux';
import $ from 'jquery';
import AhaAnalytics from 'javascripts/analytics';
import { type Placement } from 'popper.js';
import { createPortal } from 'react-dom';

interface RelatedRecordsOpenerProps {
  bookmarkId: string;
  recordId: string;
  recordType: string;
  visualizationType: 'milestone' | 'bar';
  setRelatedRecordsOpen: (isOpen: boolean) => void;
  recordEpicsEnabled: boolean;
}

const RelatedRecordsOpener: React.FC<RelatedRecordsOpenerProps> = ({
  bookmarkId,
  recordId,
  recordType,
  visualizationType,
  setRelatedRecordsOpen,
  recordEpicsEnabled,
}) => {
  const [isOpen, setIsOpenState] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const setIsOpen = useCallback(
    isOpen => {
      setRelatedRecordsOpen(isOpen);
      setIsOpenState(isOpen);

      const tooltipOpener = containerRef.current?.closest(
        '[data-tooltip-opener]'
      );
      if (isOpen && tooltipOpener) {
        // Forcibly hide aha-tooltip. Merely setting `data-title` blank does
        // not trigger it to hide.
        // @ts-ignore
        $(tooltipOpener).tooltip('hide');
      }
    },
    [setRelatedRecordsOpen]
  );

  const workspace = document.querySelector<HTMLElement>('#workspace-content');
  const [opener, setOpener] = useState<HTMLElement | null>(null);
  const [popover, setPopover] = useState<HTMLElement | null>(null);
  const [backArrow, setBackArrow] = useState<HTMLElement | null>(null);
  const [frontArrow, setFrontArrow] = useState<HTMLElement | null>(null);
  const { placement, maxHeight } = computePopoverPlacement(workspace, opener);
  const {
    styles: popperStyles,
    attributes,
    update: repositionPopper,
  } = usePopper(opener, popover, {
    placement,
    modifiers: [
      { name: 'offset', options: { offset: [0, 10] } },
      { name: 'preventOverflow', options: { padding: 25 } },
      { name: 'arrow', options: { element: backArrow } },
      { name: 'arrow', options: { element: frontArrow } },
    ],
  });
  useLayoutEffect(() => {
    // Popper has no idea about whether the element is open or not, so this
    // tells it to reposition the popper when the state changes.
    //
    // useLayoutEffect is used because this repositioning must happen before
    // the popover is painted, otherwise the user will see it jump around.
    repositionPopper && repositionPopper();
  }, [isOpen, repositionPopper]);

  useEffect(() => {
    function handleClick(event) {
      if (!containerRef.current) return;

      // If the click originated outside the container, close the popover.
      if (!containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    }

    workspace!.addEventListener('click', handleClick);

    return () => {
      workspace!.removeEventListener('click', handleClick);
    };
  }, [workspace, setIsOpen]);

  useEffect(() => {
    if (!isOpen) return;

    AhaAnalytics.track(
      'Feature-6922559282040169842',
      'User opens related records view in a custom roadmap',
      {
        'Record type': recordType,
        'Visualization type': visualizationType,
      }
    );
  }, [isOpen, recordType, visualizationType]);

  const arrowStyles = { ...popperStyles.arrow };

  // Popper mysteriously always places the arrow on top. This is a workaround.
  if (placement === 'top') {
    arrowStyles.top = '100%';
    arrowStyles.transform += ' rotate(180deg)';
  }

  return (
    <div
      className={classNames('related-records-opener-wrapper', { isOpen })}
      ref={containerRef}
    >
      <button
        className={styles.opener}
        type='button'
        onClick={() => setIsOpen(!isOpen)}
        ref={setOpener}
      >
        <i className='fa-regular fa-th-list' />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={setPopover}
            className={styles.popoverContainer}
            style={{ ...popperStyles.popper, '--max-height': `${maxHeight}px` }}
            {...attributes.popper}
          >
            <div
              ref={setBackArrow}
              style={arrowStyles}
              className={styles.backArrow}
            />
            <div
              ref={setFrontArrow}
              style={arrowStyles}
              className={styles.frontArrow}
            />

            <RelatedRecords
              {...{ bookmarkId, recordId, recordType, recordEpicsEnabled }}
            />
          </div>,
          document.body
        )}
    </div>
  );
};

export default connect(null, function mapDispatchToProps(dispatch) {
  return {
    setRelatedRecordsOpen(isOpen: boolean) {
      dispatch({ type: 'SET_RELATED_RECORDS_OPEN', payload: { isOpen } });
    },
  };
})(RelatedRecordsOpener);

/**
 * @param {Element} timeline
 * @param {Element} opener
 * @returns {{ placement: import('@popperjs/core').Placement, maxHeight: number }}
 */
function computePopoverPlacement(
  timeline: HTMLElement | null,
  opener: HTMLElement | null
): { placement: Placement; maxHeight: number } {
  if (!timeline || !opener) return { placement: 'bottom', maxHeight: 0 };

  const { top: wrapperTop, bottom: wrapperBottom } =
    timeline.getBoundingClientRect();

  const { top: openerTop, bottom: openerBottom } =
    opener.getBoundingClientRect();

  const topSpace = openerTop - wrapperTop;
  const bottomSpace = wrapperBottom - openerBottom;

  const placement = topSpace > bottomSpace ? 'top' : 'bottom';

  const minHeight = 100;
  const bottomPadding = 25;

  const maxHeight = Math.max(
    minHeight,
    Math.max(topSpace, bottomSpace) - bottomPadding
  );
  return { placement, maxHeight };
}
